<script setup lang="ts">
import { computed, reactive, ref } from "vue";
import type { Legend, NoteConfig } from "@/types/legend.types";
import type { WithoutID } from "@/types/generic.types";
import LayersConfigSection from "./LayersConfigSection.vue";
import ScalesConfigSection from "./ScalesConfigSection.vue";
import NotesConfigSection from "./NotesConfigSection.vue";
import PhotoMarkersConfigSection from "./PhotoMarkersConfigSection.vue";
import LegendDwgManager from "@/components/legends/LegendDwgManager.vue";
import {
  defaultColor,
  defaultLayer,
  Tab,
  type LegendPayload,
} from "./edit-legend.models";
import { DEFAULT_LEGEND_SETTINGS } from "@/defaultLegend";
import DRBtn from "@/components/UI/DRBtn.vue";
import { HEADER_HEIGHT } from "@/style/constants";
import { useLegendValidator } from "./use-legend-validator";
import type { Layer } from "@/types/legend.types";
import { useToast } from "@/composable/useToast";

const { showDanger } = useToast();
const props = defineProps<{
  legend: WithoutID<Legend>;
  legendId?: number;
  loading?: boolean;
  confirmLabel?: string;
}>();

// Objet Legend complet pour LegendDwgManager (uniquement en mode édition)
const legendForDwg = computed<Legend | null>(() => {
  if (!props.legendId) return null;
  return { ...props.legend, id: props.legendId } as Legend;
});

const emits = defineEmits<{
  (e: "save", legend: WithoutID<Legend>): void;
  (e: "back"): void;
}>();

const tabs = [
  { name: Tab.GENERAL, label: "dashboard:legend.main", icon: "bi-info-square" },
  { name: Tab.NOTES, label: "dashboard:legend.notes", icon: "bi-pen" },
  { name: Tab.LAYERS, label: "layers", icon: "bi-layers" },
  { name: Tab.SCALES, label: "dashboard:scales", icon: "bi-textarea-t" },
  {
    name: Tab.PHOTO_MARKERS,
    label: "dashboard:legend.photo_markers",
    icon: "bi-camera",
  },
];
const activeTab = ref(Tab.GENERAL);

const legend = reactive<LegendPayload>({
  name: props.legend.name,
  version: props.legend.version,
  note_configs: props.legend.note_configs,
  layer_configs: props.legend.layer_configs,
  scale_configs: props.legend.scale_configs,
  categories: props.legend.categories ?? [],
  settings: {
    favorite_photo_markers:
      props.legend.settings?.favorite_photo_markers ??
      DEFAULT_LEGEND_SETTINGS.favorite_photo_markers,
    photo_marker_mode:
      props.legend.settings?.photo_marker_mode ??
      DEFAULT_LEGEND_SETTINGS.photo_marker_mode,
    photo_marker_attribute:
      props.legend.settings?.photo_marker_attribute ??
      DEFAULT_LEGEND_SETTINGS.photo_marker_attribute,
    photo_marker_date_format:
      props.legend.settings?.photo_marker_date_format ??
      DEFAULT_LEGEND_SETTINGS.photo_marker_date_format,
  },
});

// Photo note computed property for the Photo Markers tab
const photoNote = computed<NoteConfig | undefined>({
  get: () => legend.note_configs.find((note) => note.type === "photo"),
  set: (value) => {
    if (!value) return;
    legend.note_configs = legend.note_configs.map((note) =>
      note.type === "photo" ? value : note
    );
  },
});

const checkLayerNameExist = (name: string, updatingIndex?: number) => {
  return legend.layer_configs.some((layer, i) => {
    if (updatingIndex !== undefined && i === updatingIndex) {
      return false;
    }
    return layer.name === name;
  });
};
// Les notes référencent un calque par son id (props.layerId), pas par son nom : l'appelant
// (NoteConfigItem / LayersConfigSection) génère l'id avant d'émettre, pour pouvoir
// sélectionner immédiatement le calque nouvellement créé sans attendre un aller-retour.
const addLayer = (payload: { id: string; name: string }) => {
  if (checkLayerNameExist(payload.name)) {
    showDanger("dashboard:alert.layer.already.exists");
    return;
  }
  legend.layer_configs = [
    ...legend.layer_configs,
    {
      ...defaultLayer,
      id: payload.id,
      name: payload.name,
    },
  ];
};

// Renommer un calque ne touche plus aucune note : elles le référencent par id (stable),
// jamais par nom.
const updateLayer = (index: number, newLayer: Layer) => {
  if (checkLayerNameExist(newLayer.name, index)) {
    showDanger("dashboard:alert.layer.already.exists");
    return;
  }

  legend.layer_configs = legend.layer_configs.map((layer, i) =>
    i === index ? newLayer : layer
  );
};

const removeLayer = (index: number) => {
  const layer = legend.layer_configs[index];
  legend.layer_configs = legend.layer_configs.filter((_, i) => i !== index);
  const layerId = layer.id;
  legend.note_configs = legend.note_configs.map((note) => {
    if (note.props.layerId === layerId) {
      return {
        ...note,
        props: {
          ...note.props,
          layerId: undefined,
          color:
            note.props.color === "kByLayer" ? defaultColor : note.props.color,
        },
      };
    }
    return note;
  });
};

const { isError, showErrors, errors } = useLegendValidator(legend);

const onSave = () => {
  if (isError.value) {
    showErrors.value = true;
    return;
  }
  emits("save", legend);
};

</script>

<template>
  <div class="mb-5 mx-3 d-flex flex-column gap-5" v-if="legend != undefined">
    <el-affix :offset="0">
      <div
        class="bg-light"
        :style="{
          marginTop: `${HEADER_HEIGHT}px`,
        }"
      >
        <ul class="nav nav-tabs">
          <li class="nav-item" v-for="tab in tabs" :key="tab.name">
            <div
              class="nav-link cursor-pointer"
              :class="[tab.name === activeTab ? 'active' : 'text-black-50']"
              aria-current="page"
              @click="activeTab = tab.name"
            >
              <div class="d-flex gap-3 align-items-center px-2 pt-1">
                <i :class="tab.icon" style="font-size: 1.4rem"></i>
                <div class="fw-medium">
                  {{ $t(tab.label) }}
                </div>
                <i
                  v-if="showErrors && !!errors[tab.name].length"
                  class="bi-exclamation-triangle-fill text-danger"
                  style="font-size: 1.2rem"
                ></i>
              </div>
            </div>
          </li>
        </ul>
      </div>
    </el-affix>

    <ul v-if="showErrors && !!errors[activeTab].length" class="text-danger">
      <li v-for="error in errors[activeTab]" :key="error">
        {{ error }}
      </li>
    </ul>

    <div v-show="activeTab === 'general'">
      <div class="d-flex gap-3 mb-4">
        <div class="col-12 col-md-6 col-lg-4">
          <div class="mb-2">{{ $t("dashboard:name.of.legend") }}</div>
          <el-input
            style="height: 3rem"
            v-model="legend.name"
            :placeholder="$t('dashboard:without.name')"
          />
        </div>
      </div>
      <LegendDwgManager v-if="legendForDwg" :legend="legendForDwg" />
    </div>
    <div v-show="activeTab === 'notes'">
      <NotesConfigSection
        v-model="legend.note_configs"
        v-model:categories="legend.categories"
        :layers="legend.layer_configs"
        @new-layer="addLayer($event)"
      />
    </div>
    <div v-show="activeTab === 'layers'">
      <LayersConfigSection
        :layers="legend.layer_configs"
        @create="addLayer($event)"
        @remove="removeLayer($event)"
        @update="updateLayer($event.index, $event.layer)"
        @reorder="legend.layer_configs = $event"
      />
    </div>
    <div v-show="activeTab === 'scales'">
      <ScalesConfigSection v-model="legend.scale_configs" />
    </div>
    <div v-show="activeTab === 'photo_markers'">
      <PhotoMarkersConfigSection
        v-model="legend.settings.favorite_photo_markers"
        v-model:photo-note="photoNote"
        v-model:photo-marker-attribute="legend.settings.photo_marker_attribute"
        v-model:photo-marker-date-format="legend.settings.photo_marker_date_format"
        :layers="legend.layer_configs"
        @new-layer="addLayer($event)"
      />
    </div>

    <el-affix position="bottom" :offset="0">
      <div
        class="d-flex justify-content-between align-items-center bg-light py-3"
      >
        <DRBtn
          variant="outline"
          icon="bi-arrow-left"
          :label="$t('back')"
          @click.prevent.stop="$emit('back')"
        />

        <div class="d-flex gap-2">
          <DRBtn
            icon="bi-save"
            :disabled="loading"
            :label="confirmLabel"
            @click.prevent.stop="onSave"
          />
        </div>
      </div>
    </el-affix>
  </div>
</template>

<style lang="scss">
.el-divider__text {
  background-color: var(--bs-light) !important;
}
</style>
